'use client'

import dynamic from 'next/dynamic'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef } from 'react'
import { IDetailRestaurant } from '@/api/restaurants/types/restaurant-types'
import { UrlConfig } from '@/setup/routing'
import { RestaurantUrlParams } from '@/setup/urls/restaurant-route'

const RestaurantVideoPlayer = dynamic(() => import('@/components/restaurant-video-player'), {
    ssr: false,
})

export const RestaurantDetailContentModalVisual = ({
    restaurant,
    citySlug,
    visualUuid,
}: {
    citySlug: string
    restaurant: IDetailRestaurant
    visualUuid?: string
}) => {
    const router = useRouter()
    const searchParams = useSearchParams()

    const visualParam =
        visualUuid ?? (searchParams.get(RestaurantUrlParams.VISUAL_UUID) || undefined)

    const handleModalClose = () => {
        if (!citySlug || !restaurant) return

        router.push(
            UrlConfig['/restaurants/[citySlug]/[restaurantSlug]'].getUrl({
                citySlug,
                restaurantSlug: restaurant.slug,
            })
        )
    }

    const matchedUuid = useMemo(
        () =>
            visualParam &&
            restaurant?.visuals?.find((visual) => {
                if (typeof visual === 'object' && visual.visualUrl) {
                    const visualUrl = getVisualUuidFromS3Key(visual.visualUrl)

                    if (visualUrl === visualParam) {
                        return visual
                    }
                }

                return null
            }),
        [restaurant, visualParam]
    )

    const safetyBelt = useRef(false)
    useEffect(() => {
        if (visualParam && !matchedUuid && restaurant) {
            if (safetyBelt.current) return

            safetyBelt.current = true

            router.replace(
                UrlConfig['/restaurants/[citySlug]/[restaurantSlug]'].getUrl({
                    citySlug,
                    restaurantSlug: restaurant.slug,
                })
            )
        }
    }, [matchedUuid, visualParam, restaurant, citySlug, router])

    useEffect(() => {
        // Make the body not scrollable when the modal is open
        const isOpen = visualParam && typeof matchedUuid === 'object' && matchedUuid?.visualUrl

        document.body.style.overflow = isOpen ? 'hidden' : 'auto'
        document.body.style.height = isOpen ? '100vh' : 'auto'

        return () => {
            document.body.style.overflow = 'auto'
            document.body.style.height = 'auto'
        }
    }, [matchedUuid, visualParam])

    if (!visualParam || !(typeof matchedUuid === 'object' && matchedUuid?.visualUrl) || !restaurant)
        return null

    return (
        <div className="fixed inset-0 z-modal bg-grey-950/40 flex items-center justify-center">
            <RestaurantVideoPlayer
                citySlug={citySlug}
                url={matchedUuid.visualUrl}
                thumbnailUrl={matchedUuid?.thumbnailUrl}
                handleClose={handleModalClose}
                restaurant={restaurant}
            />
        </div>
    )
}

function getVisualUuidFromS3Key(s3Key: string) {
    const parts = s3Key.split('/')
    const lastPart = parts[parts.length - 1]
    const matchResult = lastPart.match(
        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
    )

    return matchResult ? matchResult[0] : null
}
